import {
  noop,
  queryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { type StaticDecode, Type } from "typebox";
import * as TypeCompiler from "typebox/compile";
import {
  type LogEntry,
  useLogUpdatedCVs,
} from "@/references/context/ActivityLogs.tsx";
import { UsageUser } from "@/references/queries/projectFieldOverview.ts";
import { ReadonlyArray, ReadonlyObject } from "@/shared/schema/schema.ts";
import { http, httpChecked } from "@/shared/util/fetch.ts";
import { queryClient } from "@/shared/util/queryClient.tsx";

const ProjectDescription = ReadonlyObject({
  project_description: Type.String(),
  count: Type.Integer(),
  users: ReadonlyArray(UsageUser),
});

const checker = TypeCompiler.Compile(ReadonlyArray(ProjectDescription));

type ProjectDescription = StaticDecode<typeof ProjectDescription>;
export type ProjectDescriptions = readonly ProjectDescription[];

const queries = {
  descriptions: ["project-descriptions"] as const,
  project: (customer_id: string, project_id: string) =>
    [...queries.descriptions, { customer_id, project_id }] as const,
  fetch: (customer_id: string, project_id: string, language_code: string) =>
    queryOptions({
      queryKey: [
        ...queries.project(customer_id, project_id),
        { language_code },
      ] as const,
      queryFn: ({ signal }) =>
        httpChecked.get(
          `/api/v2/company/cv/customers/unapproved/project_experiences/project_description?language_code=${language_code}&customer_id=${customer_id}&project_id=${project_id}`,
          checker,
          { signal },
        ),
    }),
};

export function useProjectDescriptions(
  customer_id: string,
  project_id: string,
  language_code: string,
  enabled = true,
): ProjectDescriptions | undefined {
  const { data } = useQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    staleTime: 60_000,
    enabled,
  });

  return data;
}

export function usePollingProjectDescriptions(
  customer_id: string,
  project_id: string,
  language_code: string,
  enabled: boolean,
) {
  useQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    refetchInterval: 1000,
    enabled,
  });
}

interface ChangeProjectDescription {
  original_value: string;
  new_value: string;
  customer_id: string;
  project_id: string;
  language_code: string;
  limit_to_user_ids: readonly string[];
}

export function useChangeProjectDescription() {
  const logUpdatedCVs = useLogUpdatedCVs();

  return useMutation({
    mutationFn: ({ language_code, ...body }: ChangeProjectDescription) =>
      http.put<{ updated_cvs: readonly LogEntry[] }>(
        `/api/v2/update-terms/${language_code}/project_experiences/long_description`,
        { body },
      ),
    onSuccess: ({ updated_cvs }) => logUpdatedCVs(updated_cvs),
    onSettled: (
      _data,
      _error,
      { customer_id, project_id, language_code },
      _onMutateResult,
      { client },
    ) =>
      client.invalidateQueries(
        queries.fetch(customer_id, project_id, language_code),
      ),
  });
}

export async function prefetchProjectDescriptions(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  await queryClient
    .query({
      ...queries.fetch(customer_id, project_id, language_code),
      staleTime: Infinity,
    })
    // eslint-disable-next-line unicorn/prefer-await
    .catch(noop);
}

export async function invalidateProjectDescriptionsForProject(
  customer_id: string,
  project_id: string,
) {
  await queryClient.invalidateQueries({
    queryKey: queries.project(customer_id, project_id),
  });
}

export async function invalidateAllProjectDescriptions() {
  await queryClient.invalidateQueries({ queryKey: queries.descriptions });
}
